// Tipos e enums para o sistema da igreja

export const UserRole = {
  MEMBRO: "Membro",
  DIACONO: "Diacono",
  DIRETOR_PATRIMONIO: "Diretor_Patrimonio",
  PASTOR: "Pastor",
  PRESBITERO: "Presbitero",
  DESENVOLVEDOR: "desenvolvedor",
  SECRETARIO: "Secretario",
  SEGUNDO_SECRETARIO: "Segundo_Secretario",
  SEGUNDO_TESOUREIRO: "Segundo_Tesoureiro",
  TESOUREIRO: "Tesoureiro",
};

export const TipoMembro = {
  MEMBRO: "membro",
  CONGREGADO: "congregado",
  VISITANTE: "visitante",
  FREQUENTADOR: "frequentador",
};

export const Sexo = {
  MASCULINO: "masculino",
  FEMININO: "feminino",
};

export const EstadoCivil = {
  SOLTEIRO: "solteiro",
  CASADO: "casado",
  DIVORCIADO: "divorciado",
  VIUVO: "viuvo",
  UNIAO_ESTAVEL: "uniao_estavel",
};

// Interfaces removidas - usando JSDoc para documentação

/**
 * Interface Membro
 * @typedef {Object} Membro
 * @property {number} id - Auto increment
 * @property {string} nome
 * @property {Date} data_nascimento
 * @property {string} telefone
 * @property {string} [email] - Opcional
 * @property {string} endereco
 * @property {Date} data_entrada
 * @property {boolean} ativo
 * @property {string} cpf
 * @property {string} tipo - TipoMembro
 * @property {string} sexo - Sexo
 * @property {string} [nome_pai]
 * @property {string} [nome_mae]
 * @property {string} estado_civil - EstadoCivil
 * @property {Date} [data_casamento]
 * @property {string} [nome_conjuge]
 * @property {Date} [data_nascimento_conjuge]
 * @property {Date} [data_batismo]
 * @property {boolean} possui_filhos - Indica se o membro possui filhos
 * @property {Date} created_at
 * @property {Date} updated_at
 * @property {string} created_by - UID do usuário que criou
 * @property {string} updated_by - UID do usuário que atualizou
 */

/**
 * Interface ChurchUser
 * @typedef {Object} ChurchUser
 * @property {string} uid
 * @property {string} email
 * @property {string} [displayName]
 * @property {string} role - UserRole
 * @property {number} [membro_id] - Referência ao membro se houver
 * @property {Date} createdAt
 * @property {Date} updatedAt
 */

// Export do ChurchUser para compatibilidade
export const ChurchUser = {};

// Hierarquia de permissões (do menor para o maior)
export const ROLE_HIERARCHY = {
  [UserRole.MEMBRO]: 1,
  [UserRole.DIACONO]: 2,
  [UserRole.SEGUNDO_TESOUREIRO]: 3,
  [UserRole.SEGUNDO_SECRETARIO]: 4,
  [UserRole.TESOUREIRO]: 5,
  [UserRole.SECRETARIO]: 6,
  [UserRole.DIRETOR_PATRIMONIO]: 7,
  [UserRole.PRESBITERO]: 8,
  [UserRole.PASTOR]: 9,
  [UserRole.DESENVOLVEDOR]: 10,
};

// Função para verificar se um role tem permissão sobre outro
export const hasPermission = (userRole, requiredRole) => {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
};

// Função para determinar o role baseado no email
const DEV_EMAIL = import.meta.env.VITE_DESENVOLVEDOR_EMAIL;
export const determineUserRole = (email) => {
  // Email do desenvolvedor (definido no .env)
  if (DEV_EMAIL && email?.toLowerCase() === DEV_EMAIL.toLowerCase()) {
    return UserRole.DESENVOLVEDOR;
  }

  // Todos os outros começam como Membro
  return UserRole.MEMBRO;
};

//  Labels amigáveis para exibição
export const ROLE_LABELS = {
  [UserRole.MEMBRO]: "Membro",
  [UserRole.DIACONO]: "Diácono",
  [UserRole.DIRETOR_PATRIMONIO]: "Diretor de Patrimônio",
  [UserRole.PASTOR]: "Pastor",
  [UserRole.PRESBITERO]: "Presbítero",
  [UserRole.DESENVOLVEDOR]: "Desenvolvedor",
  [UserRole.SECRETARIO]: "Secretário",
  [UserRole.SEGUNDO_SECRETARIO]: "Segundo Secretário",
  [UserRole.SEGUNDO_TESOUREIRO]: "Segundo Tesoureiro",
  [UserRole.TESOUREIRO]: "Tesoureiro",
};

export const TIPO_MEMBRO_LABELS = {
  [TipoMembro.MEMBRO]: "Membro",
  [TipoMembro.CONGREGADO]: "Congregado",
  [TipoMembro.VISITANTE]: "Visitante",
  [TipoMembro.FREQUENTADOR]: "Frequentador",
};

export const SEXO_LABELS = {
  [Sexo.MASCULINO]: "Masculino",
  [Sexo.FEMININO]: "Feminino",
};

export const ESTADO_CIVIL_LABELS = {
  [EstadoCivil.SOLTEIRO]: "Solteiro(a)",
  [EstadoCivil.CASADO]: "Casado(a)",
  [EstadoCivil.DIVORCIADO]: "Divorciado(a)",
  [EstadoCivil.VIUVO]: "Viúvo(a)",
  [EstadoCivil.UNIAO_ESTAVEL]: "União Estável",
};

/**
 * Interface Patrimonio
 * @typedef {Object} Patrimonio
 * @property {string} id - ID do documento
 * @property {string} codigo - Código de identificação
 * @property {string} nome
 * @property {string} tipo - Tipo de patrimônio (movel, imovel, equipamento, veiculo, instrumento_musical, tecnologia, outros)
 * @property {string} descricao
 * @property {number} valor_aquisicao
 * @property {Date} data_aquisicao
 * @property {string} localizacao
 * @property {string} [fornecedor]
 * @property {string} [nota_fiscal]
 * @property {number} [valor_residual]
 * @property {number} [vida_util_anos]
 * @property {string} [metodo_depreciacao] - linear, soma_digitos, saldo_decrescente
 * @property {string} status - ativo, em_manutencao, depreciado, baixado
 * @property {string} [imagem] - URL da imagem
 * @property {string} [observacoes]
 * @property {Date} created_at
 * @property {Date} updated_at
 * @property {string} created_by - UID do usuário que criou
 * @property {string} updated_by - UID do usuário que atualizou
 */

/**
 * Interface Irregularidade
 * @typedef {Object} Irregularidade
 * @property {string} id - ID do documento
 * @property {string} tipo - Tipo de irregularidade (estrutural, eletrica, hidraulica, pintura, limpeza, equipamento, seguranca, outros)
 * @property {string} localizacao - Descrição da localização no prédio
 * @property {string} descricao - Descrição detalhada do problema
 * @property {string} prioridade - baixa, media, alta, urgente
 * @property {string} status - pendente, em_analise, em_andamento, resolvida, cancelada
 * @property {string[]} [imagens] - URLs das imagens
 * @property {Array<{status: string, observacao: string, data: Date, usuario: string}>} [historico] - Histórico de mudanças de status
 * @property {Date} created_at
 * @property {Date} updated_at
 * @property {string} created_by - UID do usuário que reportou
 * @property {string} created_by_name - Nome do usuário que reportou
 * @property {string} updated_by - UID do usuário que atualizou
 */
